import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';
import { normalizeSearchForm, normalizeSearchTerm } from '@theme/search-normalization';

/**
 * A custom element that allows the user to clean a search input.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchPageInput - The search input element.
 * @extends {Component<Refs>}
 */
class SearchPageInputComponent extends Component {
  requiredRefs = ['searchPageInput'];

  #controller = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    const form = this.closest('form') ?? this.refs.searchPageInput.form;
    form?.addEventListener('submit', this.#handleFormSubmit, { signal: this.#controller.signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Handles the keydown event on the search input and resets the search when
   * empty and Escape is pressed.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown = debounce((event) => {
    const value = this.refs.searchPageInput.value.trim();

    if (event.key === 'Escape' && value === '') {
      this.#submitEmptySearch();
    }
  }, 100);

  /**
   * @param {SubmitEvent} event
   */
  #handleFormSubmit = (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const normalized = normalizeSearchForm(form);
    if (!normalized) return;

    // Keep the visible input in sync if the synonym map changed the term
    if (this.refs.searchPageInput.value !== normalized) {
      this.refs.searchPageInput.value = normalized;
    }
  };

  #submitEmptySearch() {
    const searchInput = this.refs.searchPageInput;

    searchInput.focus();
    searchInput.value = '';

    if (this.#isEmptyState()) return;

    searchInput.form?.submit();
  }

  #isEmptyState = () => {
    const url = new URL(window.location.href);
    const queryParam = url.searchParams.get('q') ?? '';

    return normalizeSearchTerm(queryParam).trim() === '';
  };
}

if (!customElements.get('search-page-input-component')) {
  customElements.define('search-page-input-component', SearchPageInputComponent);
}
